import React from 'react';

import {
  AccountCircle as AccountCircleIcon,
  ArrowDropDownOutlined as ArrowDropDownIcon,
  Menu as MenuIcon,
  NavigateBefore as NavigateBeforeIcon,
  PowerSettingsNew as PowerSettingsIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import {
  AppBar,
  Avatar,
  Box,
  Breadcrumbs,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Switch,
  Toolbar,
  Typography,
} from '@mui/material';
import { Link, matchPath, useLocation, useNavigate } from 'react-router';

import type { Nullable } from '@clearverify/types';

import type { CurrentUserBaseInfoFragment } from 'src/api';
import { BreadcrumbPath, Button, Popover } from 'src/components/common';
import { useScreenInfo } from 'src/hooks/common';
import { useDemoPrivacyMode } from 'src/hooks/demoPrivacyMode';
import { OpenCloseStateResult } from 'src/hooks/shared';
import { useCurrentUserPermissions } from 'src/hooks/user';
import type { BreadCrumb } from 'src/types/router';
import { getUserFullName } from 'src/utils/user';

import * as styles from './styles';
import type { LayoutHeaderTopAction } from './types';

interface BaseLayoutHeaderProps {
  rootRoute: string;
  accountRoute: string;
  logoutRoute: string;
  breadCrumbs: Array<BreadCrumb>;
  currentUser: Nullable<CurrentUserBaseInfoFragment>;
  currentUserAvatarUrl?: Nullable<string>;
  defaultLogoUrl: string;
  isMenuDrawerDisabled?: boolean;
  leftMenuDrawerState: OpenCloseStateResult<Record<string, never>>;
  topAction?: LayoutHeaderTopAction;
  leftLogo?: React.ReactNode;
  inboxItemTypeTitle?: string;
  loadingPageTitle?: boolean;
}

export const BaseLayoutHeader: React.FC<BaseLayoutHeaderProps> = ({
  rootRoute,
  accountRoute,
  logoutRoute,
  breadCrumbs,
  currentUser,
  currentUserAvatarUrl,
  defaultLogoUrl,
  isMenuDrawerDisabled,
  leftMenuDrawerState,
  topAction,
  leftLogo,
  inboxItemTypeTitle,
  loadingPageTitle = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useScreenInfo();

  const isRootPage = Boolean(matchPath({ path: rootRoute }, location.pathname));
  const isExactHomePath = Boolean(matchPath({ path: rootRoute, end: false }, location.pathname));

  const { isPlatformAdmin } = useCurrentUserPermissions();
  const { isDemoPrivacyModeAvailable, isDemoPrivacyModeEnabled, setDemoPrivacyModeEnabled } =
    useDemoPrivacyMode();

  const userAvatar = React.useMemo(
    () => currentUserAvatarUrl || defaultLogoUrl,
    [currentUserAvatarUrl, defaultLogoUrl],
  );

  const handleTopLeftButtonClick = React.useCallback(() => {
    leftMenuDrawerState.toggle();
  }, [leftMenuDrawerState]);

  const renderMobileBreadCrumb = React.useCallback(() => {
    if (isRootPage && !leftMenuDrawerState.isOpened) {
      return <Box sx={styles.logoStyles}>{leftLogo}</Box>;
    }

    const matchedBreadCrumbs = breadCrumbs.filter(breadcrumbRoute => {
      return Boolean(matchPath({ path: breadcrumbRoute.route, end: false }, location.pathname));
    });
    const currentBreadCrumb = matchedBreadCrumbs[matchedBreadCrumbs.length - 1];
    if (!currentBreadCrumb) {
      return <Box sx={styles.logoStyles}>{leftLogo}</Box>;
    }

    return (
      <React.Fragment>
        <IconButton onClick={() => navigate(rootRoute)} color="default" sx={styles.returnBtnStyles}>
          <NavigateBeforeIcon sx={styles.returnBtnIconStyles} />
        </IconButton>
        {loadingPageTitle ? (
          <Skeleton animation="wave" height={35} width="220px" />
        ) : (
          <Typography variant="h5" sx={{ color: '#000' }}>
            {inboxItemTypeTitle || currentBreadCrumb.title}
          </Typography>
        )}
      </React.Fragment>
    );
  }, [
    breadCrumbs,
    inboxItemTypeTitle,
    isRootPage,
    leftMenuDrawerState.isOpened,
    leftLogo,
    loadingPageTitle,
    location.pathname,
    navigate,
    rootRoute,
  ]);

  const breadcrumbPaths = React.useMemo(() => {
    return breadCrumbs
      .filter(breadcrumbRoute => {
        return Boolean(matchPath({ path: breadcrumbRoute.route, end: false }, location.pathname));
      })
      .map(breadcrumbRoute => {
        return (
          <BreadcrumbPath
            key={breadcrumbRoute.route}
            title={breadcrumbRoute.title}
            path={breadcrumbRoute.route}
          />
        );
      });
  }, [breadCrumbs, location.pathname]);

  const renderPopoverContent = React.useCallback(
    // eslint-disable-next-line react/no-unused-prop-types
    ({ onClose }: { onClose: () => void }): React.ReactNode => {
      const handleSignOutClick = async () => {
        navigate(logoutRoute);
      };

      return (
        <Box sx={styles.popoverContentStyles}>
          <Box sx={styles.popoverContentTitleBoxStyles}>
            <Avatar variant="circular" src={userAvatar} />
            <Typography variant="body1">Hello, {currentUser?.firstName}</Typography>
          </Box>

          <List sx={styles.popoverListStyles}>
            <ListItem disablePadding>
              <ListItemButton component={Link} onClick={onClose} to={accountRoute}>
                <ListItemIcon>
                  <AccountCircleIcon />
                </ListItemIcon>
                <ListItemText primary="My account" />
              </ListItemButton>
            </ListItem>

            {isDemoPrivacyModeAvailable && (
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => setDemoPrivacyModeEnabled(!isDemoPrivacyModeEnabled)}
                  sx={{ py: 0.75, minHeight: 0 }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <VisibilityOffIcon sx={{ fontSize: 20 }} />
                  </ListItemIcon>
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                    Demo mode
                  </Typography>
                  <Switch
                    size="small"
                    edge="end"
                    checked={isDemoPrivacyModeEnabled}
                    onChange={(_, checked) => setDemoPrivacyModeEnabled(checked)}
                    onClick={event => event.stopPropagation()}
                  />
                </ListItemButton>
              </ListItem>
            )}

            <ListItem disablePadding>
              <ListItemButton onClick={handleSignOutClick} sx={styles.signOutButtonStyles}>
                <ListItemIcon>
                  <PowerSettingsIcon />
                </ListItemIcon>
                <ListItemText primary="Sign Out" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      );
    },
    [
      accountRoute,
      currentUser?.firstName,
      isDemoPrivacyModeAvailable,
      isDemoPrivacyModeEnabled,
      logoutRoute,
      navigate,
      setDemoPrivacyModeEnabled,
      userAvatar,
    ],
  );

  const topActionContent = React.useMemo(() => {
    if (!topAction) return null;

    if (topAction.loading) {
      return <Skeleton width={200} />;
    }

    return (
      <Box
        height="100%"
        display="flex"
        alignItems="center"
        paddingRight="8px"
        borderRight="1px solid rgba(0, 0, 0, 0.12)"
      >
        <Button
          variant="text"
          component={topAction.href ? Link : undefined}
          to={topAction.href ?? '/'}
          onClick={topAction.onClick}
          color={topAction.color}
          startIcon={topAction.icon}
        >
          {topAction.label}
        </Button>
      </Box>
    );
  }, [topAction]);

  if (isMobile) {
    return (
      <AppBar
        sx={{ backgroundColor: !isExactHomePath ? '#fff' : 'slate.50' }}
        position="relative"
        elevation={0}
      >
        <Toolbar sx={styles.topbarStyles}>
          <Box sx={styles.mobileTopbarLeftContentStyles}>{renderMobileBreadCrumb()}</Box>

          <Box sx={styles.topbarRightContentStyles}>
            <IconButton
              disabled={isMenuDrawerDisabled}
              onClick={handleTopLeftButtonClick}
              color="default"
              size="medium"
              sx={{ marginBottom: '2px' }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
    );
  }

  return (
    <AppBar
      color="transparent"
      sx={{ boxShadow: '0px 2px 14px 0px rgba(23, 23, 26, 0.1)' }}
      position="relative"
    >
      <Toolbar sx={styles.topbarStyles}>
        <Box sx={styles.topbarLeftContentStyles}>
          <IconButton
            disabled={isMenuDrawerDisabled}
            onClick={handleTopLeftButtonClick}
            color="default"
            size="medium"
          >
            <MenuIcon />
          </IconButton>

          <Breadcrumbs
            expandText="Show path"
            itemsAfterCollapse={1}
            itemsBeforeCollapse={1}
            maxItems={8}
            separator="/"
          >
            {isRootPage && !isPlatformAdmin && <Typography variant="body1">Home</Typography>}
            {breadcrumbPaths}
          </Breadcrumbs>
        </Box>

        <Box sx={styles.topbarRightContentStyles}>
          {topActionContent}

          <Avatar src={userAvatar} variant="circular" />

          <Typography variant="body1">{getUserFullName(currentUser)}</Typography>

          <Popover
            target={
              <IconButton size="medium" color="default" data-e2e-element="profile-menu">
                <ArrowDropDownIcon />
              </IconButton>
            }
            content={renderPopoverContent}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            elevation={4}
          />
        </Box>
      </Toolbar>
    </AppBar>
  );
};
